import { DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { InputDatePicker } from '@retail/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { isToday } from 'date-fns';
import { useState } from 'react';
import { TodayToggleButton } from '../TodayToggleButton';

interface Props {
  tagName: string;
  minDate?: Date;
  isSaving?: boolean;
  onConfirm: (removedFrom: Date | null) => void;
  onClose: () => void;
}

export function RemoveFromSubAssortmentDialog({
  tagName,
  minDate,
  isSaving,
  onConfirm,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const [removedFrom, setRemovedFrom] = useState<Date | null>(minDate ?? new Date());

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('myAssortment.centralAssortments.deleteSupplierItemAssortmentTag')}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          {t('myAssortment.centralAssortments.removeFromSubAssortmentDescription', {
            assortmentTag: tagName,
          })}
        </Typography>

        <Stack flexDirection="row" gap={2} alignItems="flex-end">
          <InputDatePicker
            name="removedFrom"
            value={removedFrom}
            onChange={setRemovedFrom}
            label={t('myAssortment.centralAssortments.removedFromDate')}
            minDate={minDate ?? new Date()}
            slotProps={{ field: { clearable: true } }}
            margin="none"
            size="small"
          />
          <TodayToggleButton
            selected={!!removedFrom && isToday(removedFrom)}
            selectToday={() => setRemovedFrom(new Date())}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('myAssortment.centralAssortments.removeImmediatelyHint')}
        </Typography>
      </DialogContent>

      <DialogActions>
        <OutlinedButton size="small" onClick={onClose}>
          {t('myAssortment.cancel')}
        </OutlinedButton>
        <ContainedButton
          color="warning"
          size="small"
          loading={isSaving}
          onClick={() => onConfirm(removedFrom)}
        >
          {t('myAssortment.centralAssortments.removeItem')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
